import { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, X, CalendarDays } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { SupportChatPanel } from '@/components/SupportChatPanel';
import { supabase } from '@/lib/supabase';
import { SupportMessageRow, fetchUserUnreadCount } from '@/services/supportService';

const CAL_LINK = 'https://calendly.com/estage-vision/30min';

interface HelpButtonProps {
  /** Flag-gesteuert: true = Support-Chat, false = bisherige Calendly-Karte. */
  chatEnabled: boolean;
}

export const HelpButton: React.FC<HelpButtonProps> = ({ chatEnabled }) => {
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const openRef = useRef(open);
  openRef.current = open;

  // Badge beim App-Start laden (nur wenn Chat aktiv)
  useEffect(() => {
    if (!chatEnabled) return;
    fetchUserUnreadCount().then(setUnread).catch(() => {});
  }, [chatEnabled]);

  // Realtime: neue Admin-Antworten bumpen das Badge auch bei geschlossenem Panel
  // (Publication: Migration 122). Das Event ist nur der Trigger — gezählt wird über
  // fetchUserUnreadCount (auf eigene Tickets gescoped), damit Admin-Accounts nicht
  // fremde Tickets mitzählen. Bei offenem Panel übernimmt SupportChatPanel.
  useEffect(() => {
    if (!chatEnabled) return;
    const channel = supabase
      .channel('support-unread-badge')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'support_messages' },
        (payload) => {
          const row = payload.new as SupportMessageRow;
          if (row.sender_role !== 'admin' || openRef.current) return;
          fetchUserUnreadCount().then(setUnread).catch(() => {});
        },
      )
      .subscribe();
    return () => { void supabase.removeChannel(channel); };
  }, [chatEnabled]);

  return (
    <div className="fixed bottom-6 right-6 z-40 flex flex-col items-end gap-3">
      <AnimatePresence>
        {open && (
          <motion.div
            key="help-card"
            initial={{ opacity: 0, y: 8, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 8, scale: 0.97 }}
            transition={{ duration: 0.18, ease: 'easeOut' }}
            className={`rounded-2xl border border-border bg-card shadow-xl p-5 ${chatEnabled ? 'w-80 sm:w-96' : 'w-72'}`}
          >
            <div className="flex items-start justify-between gap-2 mb-3">
              <div className="flex items-center gap-2">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10">
                  <MessageCircle className="h-4 w-4 text-primary" />
                </div>
                <span className="text-sm font-semibold text-foreground">Support</span>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="text-muted-foreground hover:text-foreground transition-colors mt-0.5"
                aria-label="Schließen"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {chatEnabled ? (
              <SupportChatPanel onUnreadCleared={() => setUnread(0)} />
            ) : (
              <>
                <p className="text-sm text-foreground font-medium leading-snug mb-1">
                  Haben Sie Fragen, oder brauchen Sie Hilfe?
                </p>
                <p className="text-xs text-muted-foreground mb-4">
                  Vereinbaren Sie jetzt einen kostenlosen Beratungstermin.
                </p>
                <Button asChild className="w-full gap-2" size="sm">
                  <a href={CAL_LINK} target="_blank" rel="noopener noreferrer">
                    <CalendarDays className="h-4 w-4" />
                    Termin vereinbaren
                  </a>
                </Button>
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      <motion.button
        type="button"
        onClick={() => setOpen(prev => !prev)}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.97 }}
        className="relative flex items-center gap-2 rounded-full bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground shadow-lg shadow-primary/30 transition-colors hover:bg-primary/90"
        aria-label="Hilfe & Kontakt"
      >
        <MessageCircle className="h-4 w-4" />
        <span className="hidden sm:inline">Hilfe & Kontakt</span>
        {chatEnabled && unread > 0 && !open && (
          <span className="absolute -top-1.5 -right-1.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-destructive px-1 text-[11px] font-bold text-destructive-foreground">
            {unread}
          </span>
        )}
      </motion.button>
    </div>
  );
};
